import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import {
  ChartBarIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  LightBulbIcon,
  ClockIcon,
  HandRaisedIcon,
  BuildingOfficeIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

function About() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  const stats = [
    { number: "15+", text: "Years Experience" },
    { number: "$2.5B+", text: "Assets Managed" },
    { number: "50K+", text: "Happy Clients" },
    { number: "98%", text: "Client Retention" }
  ];

  const values = [
    {
      icon: ShieldCheckIcon,
      title: "Trust",
      description: "Building lasting relationships through transparency and integrity"
    },
    {
      icon: LightBulbIcon,
      title: "Innovation",
      description: "Leveraging cutting-edge technology for optimal results"
    },
    {
      icon: HandRaisedIcon,
      title: "Commitment",
      description: "Dedicated to your financial success and security"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <motion.section 
        className="relative pt-24 pb-32 bg-primary-900 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary-800 to-primary-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Your Trusted Investment Partner
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Since 2008, we've been helping investors build and protect their wealth 
              through innovative investment strategies and personalized service.
            </p>
          </motion.div>
        </div>
        
        {/* Stats */}
        <motion.div 
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-primary-200">{stat.text}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.section>

      {/* Our Story Section */}
      <motion.section 
        className="py-20 bg-white"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded in the midst of the 2008 financial crisis, we emerged with a 
                clear vision: to revolutionize investment management through transparency, 
                technology, and trust.
              </p>
              <p className="text-lg text-gray-600">
                Today, we're proud to serve thousands of clients worldwide, helping them 
                navigate the complexities of modern investing with confidence and clarity.
              </p>
            </motion.div>
            <motion.div
              className="relative h-[400px] rounded-2xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="/images/about1.jpg"
                alt="Our journey"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Our Values */}
      <motion.section 
        className="py-20 bg-gray-900 text-white"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Our Core Values</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              These principles guide every decision we make and every relationship we build.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl"
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
              >
                <value.icon className="h-12 w-12 text-primary-500 mb-6" />
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Investment Philosophy */}
      <motion.section 
        className="py-20 bg-white"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Investment Philosophy</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-primary-600 mt-1" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold mb-2">Data-Driven Decisions</h3>
                    <p className="text-gray-600">Leveraging advanced analytics and market research for optimal investment strategies.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <GlobeAltIcon className="h-6 w-6 text-primary-600 mt-1" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold mb-2">Global Perspective</h3>
                    <p className="text-gray-600">Diversifying across markets and sectors to maximize opportunities.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6 text-primary-600 mt-1" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold mb-2">Long-Term Focus</h3>
                    <p className="text-gray-600">Building sustainable wealth through strategic, long-term investment planning.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="order-1 lg:order-2 relative h-[500px] rounded-2xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="/images/about3.jpg"
                alt="Investment Philosophy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Technology & Innovation */}
      <motion.section 
        className="py-20 bg-gray-900 text-white"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Technology & Innovation</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Leveraging cutting-edge technology to provide superior investment solutions
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "AI-Powered Analytics",
                description: "Advanced algorithms analyzing market trends and opportunities"
              },
              {
                title: "Secure Platform",
                description: "State-of-the-art security protecting your investments"
              },
              {
                title: "Real-Time Monitoring",
                description: "24/7 portfolio tracking and instant updates"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-xl border border-gray-700"
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
              >
                <h3 className="text-xl font-semibold mb-4">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Global Presence */}
      <motion.section 
        className="py-20 bg-white overflow-hidden"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Global Presence</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Operating across major financial hubs to serve clients worldwide
            </p>
          </div>
          <motion.div
            className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="/images/about2.jpg"
              alt="Global Presence"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          </motion.div>
        </div>
      </motion.section>

      {/* Awards & Recognition */}
      <motion.section 
        className="py-20 bg-gray-50"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Awards & Recognition</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Excellence recognized by leading industry authorities
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((_, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <TrophyIcon className="h-12 w-12 text-primary-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Best Investment Firm {2023 - index}
                </h3>
                <p className="text-sm text-gray-500 text-center">Financial Excellence Awards</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Call to Action */}
      <motion.section 
        className="py-20 bg-primary-900 text-white"
        {...fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Your Investment Journey?</h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of successful investors who trust us with their financial future
          </p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            viewport={{ once: true }}
          >
            <Link to="/contact">
              <motion.button
                className="px-8 py-3 bg-white text-primary-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.button>
            </Link>
            <Link to="/portfolio">
              <motion.button
                className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Portfolio
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}

export default About; 